import { Fragment } from 'react';
import { Listbox, Transition } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';
import { clsx } from 'clsx';
import { Model, AccountUsage } from '../types';

/**
 * The AI model picker.
 *
 * A Headless UI `Listbox` rather than a native `<select>`, for one reason: an
 * `<option>` accepts plain text only — browsers strip markup inside it — so a native
 * select cannot show the model name and its details as two differently-weighted lines.
 * That is the whole point here. Cost per 1,000 tokens is accurate and unusable; what a
 * customer can act on is what the model costs per message and how much of their plan it
 * buys, and that does not fit on one line next to the name.
 *
 * The stakes are real rather than cosmetic: credits per conversation span more than 7x
 * across the four models offered, so a customer switching model can go from ~640
 * conversations a month to ~70 and only discover it when the bot goes dark mid-month.
 *
 * All the arithmetic arrives from `/models` and `/user/usage`. Nothing here computes a
 * credit figure — that logic lives next to the rates it depends on, server-side.
 */

/** Below this many conversations a day, a bot on a busy site runs out mid-month. */
const LOW_CAPACITY_PER_DAY = 10;
const DAYS_PER_MONTH = 30;

function conversationsPerMonth(model?: Model, usage?: AccountUsage | null): number | null {
  const credits = model?.creditsPerConversation;
  const monthly = usage?.monthlyCredits;
  if (!model || !credits || !monthly) return null;
  return Math.round(monthly / credits);
}

/** "about 640" — never an exact-looking number, because it is not an exact quantity. */
function approx(n: number): string {
  if (n >= 1000) return `about ${(Math.round(n / 100) * 100).toLocaleString()}`;
  if (n >= 100) return `about ${Math.round(n / 10) * 10}`;
  return `about ${n}`;
}

/**
 * The grey second line: what the model is, what a message costs, and — only when the
 * plan actually has a recurring grant — how many conversations that buys.
 *
 * The capacity clause is omitted rather than zeroed on Free, which has no recurring
 * grant at all (its welcome credits are seeded once and never reset). Showing "0
 * conversations a month" there would be false, and showing a monthly figure at all
 * would imply a renewal that never happens.
 */
function detailLine(model: Model, usage?: AccountUsage | null): string {
  const parts: string[] = [];
  if (model.description) parts.push(model.description);
  if (model.creditsPerMessageLow != null && model.creditsPerMessageHigh != null) {
    parts.push(`≈${model.creditsPerMessageLow}–${model.creditsPerMessageHigh} credits a message`);
  }
  const perMonth = conversationsPerMonth(model, usage);
  if (perMonth !== null) parts.push(`${approx(perMonth)} conversations a month`);
  return parts.join(' · ');
}

interface Props {
  models: Model[];
  modelsLoading: boolean;
  value: string;
  /** A plain callback rather than a synthetic change event: this is a listbox, not an
   *  input, and manufacturing a fake ChangeEvent to satisfy the form's shared handler
   *  would be a lie about where the value came from. */
  onSelect: (modelId: string) => void;
  usage: AccountUsage | null;
}

export default function ModelField({ models, modelsLoading, value, onSelect, usage }: Props) {
  const selected = models.find(m => m.id === value);
  const fallback = models.find(m => m.isDefault) ?? models[0];
  const perMonth = conversationsPerMonth(selected, usage);
  const defaultPerMonth = conversationsPerMonth(fallback, usage);
  const perDay = perMonth !== null ? perMonth / DAYS_PER_MONTH : null;

  // Warn on the absolute figure rather than on "worse than the default": what predicts
  // a customer going dark is how few conversations they actually get, not how the
  // choice ranks against another one.
  const isLow = perDay !== null && perDay < LOW_CAPACITY_PER_DAY;
  const betterExists =
    isLow && defaultPerMonth !== null && perMonth !== null && defaultPerMonth > perMonth;

  return (
    <div className="lg:col-span-1">
      <Listbox
        value={value}
        onChange={onSelect}
        disabled={modelsLoading || models.length === 0}
      >
        <Listbox.Label className="block text-sm text-gray-500 dark:text-gray-400 mb-2">
          AI Model
        </Listbox.Label>

        <div className="relative">
          <Listbox.Button className="relative w-full cursor-pointer rounded-xl border border-gray-200 bg-white py-2.5 pl-4 pr-10 text-left text-sm font-medium text-gray-900 transition-all duration-200 hover:border-gray-300 focus:border-gray-400 focus:outline-none disabled:opacity-50 dark:border-slate-600 dark:bg-slate-900 dark:text-gray-100 dark:hover:border-slate-500 dark:focus:border-slate-500">
            <span className="block truncate">
              {modelsLoading
                ? 'Loading models…'
                : selected?.name ?? (models.length ? 'Select a model' : 'No models available')}
            </span>
            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
              <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
            </span>
          </Listbox.Button>

          <Transition
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Listbox.Options className="absolute z-50 mt-1 max-h-80 w-full overflow-auto rounded-xl border border-gray-200 bg-white py-1 shadow-lg focus:outline-none dark:border-slate-600 dark:bg-slate-800">
              {models.map(model => (
                <Listbox.Option
                  key={model.id}
                  value={model.id}
                  className={({ active }) =>
                    clsx(
                      'relative cursor-pointer select-none py-2.5 pl-4 pr-10',
                      active ? 'bg-gray-50 dark:bg-slate-700/60' : ''
                    )
                  }
                >
                  {({ selected: isSelected }) => (
                    <>
                      <span
                        className={clsx(
                          'block text-sm text-gray-900 dark:text-gray-100',
                          isSelected ? 'font-semibold' : 'font-medium'
                        )}
                      >
                        {model.name}
                      </span>
                      {/* The detail line wraps rather than truncating: it carries the
                          cost, which is the reason this control is not a native select
                          in the first place. Truncating it would defeat the change. */}
                      <span className="mt-0.5 block text-xs leading-relaxed text-gray-500 dark:text-gray-400">
                        {detailLine(model, usage)}
                      </span>
                      {isSelected && (
                        <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-900 dark:text-gray-100">
                          <CheckIcon className="h-5 w-5" aria-hidden="true" />
                        </span>
                      )}
                    </>
                  )}
                </Listbox.Option>
              ))}
            </Listbox.Options>
          </Transition>
        </div>
      </Listbox>

      {perMonth !== null && selected && (
        <p
          className={clsx(
            'mt-2 text-xs',
            isLow ? 'text-amber-700 dark:text-amber-400' : 'text-gray-500 dark:text-gray-400'
          )}
        >
          {approx(perMonth)} conversations a month on your plan
          {selected.questionsPerConversation
            ? ` — a conversation is about ${selected.questionsPerConversation} questions`
            : ''}
          {'. '}
          {/* Always hedged. Cost per answer tracks how much content the bot reads, and
              that varies by more than 10x across real sites, so a figure presented as
              exact would be a promise we cannot keep. */}
          Varies with how much content your bot reads.
          {isLow && (
            <>
              {' '}
              <strong className="font-medium">
                That is roughly {perDay!.toFixed(perDay! < 10 ? 1 : 0)} a day.
              </strong>{' '}
              {betterExists
                ? `${fallback!.name} would give ${approx(defaultPerMonth!)} on the same plan.`
                : 'Consider a larger plan if your site is busy.'}
            </>
          )}
        </p>
      )}
    </div>
  );
}
